import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { useMutation } from './mutation.js';

/**
 * Accès de Venio à la galerie : le mandat de reprise (`console/mandat.ts`,
 * `docs/dev/api.md` § 12.13) et l'accès de support (§ 12.5). Les deux se
 * tranchent ici, par la direction, en session humaine : jamais par un jeton.
 */

export type NatureMandat =
  | 'oeuvres'
  | 'lieux'
  | 'artistes'
  | 'contacts'
  | 'expositions'
  | 'factures-historiques'
  | 'documents'
  | 'images'
  | 'localisations'
  | 'provenance'
  | 'editions'
  | 'membres';

export type EtatMandat = 'en_attente' | 'actif' | 'refuse' | 'revoque' | 'remplace';

export interface MandatReprise {
  readonly id: string;
  readonly etat: EtatMandat;
  /** `contrat` : posé par Venio au contrat ; `galerie` : accordé dans la régie. */
  readonly source: 'contrat' | 'galerie';
  readonly natures: readonly NatureMandat[];
  readonly confidentiels: boolean;
  readonly reference: string;
  readonly motif: string;
  readonly dureeJours: number | null;
  readonly demandePar: string | null;
  readonly accordePar: string | null;
  readonly demandeLe: string;
  readonly accordeLe: string | null;
  /** En attente : péremption de la demande ; actif : échéance du mandat. */
  readonly expireLe: string;
  readonly revoqueLe: string | null;
  readonly revoqueDepuis: 'console' | 'galerie' | null;
}

export interface EtatMandatReprise {
  readonly actif: MandatReprise | null;
  readonly demandes: readonly MandatReprise[];
  readonly historique: readonly MandatReprise[];
}

export type EtatDemandeSupport = 'en_attente' | 'accordee' | 'refusee' | 'revoquee';

export interface DemandeSupport {
  readonly id: string;
  readonly demandeurEmail: string | null;
  readonly motif: string;
  readonly role: 'lecture' | 'equipe';
  readonly etat: EtatDemandeSupport;
  readonly demandeLe: string;
  readonly expireLe: string;
  readonly trancheLe: string | null;
}

export interface AccesSupport {
  readonly id: string;
  readonly supportEmail: string | null;
  readonly motif: string;
  readonly role: 'lecture' | 'equipe';
  readonly accordeLe: string;
  readonly expireLe: string;
  readonly revoqueLe: string | null;
  readonly actif: boolean;
}

export interface EtatSupport {
  readonly demandes: readonly DemandeSupport[];
  readonly acces: readonly AccesSupport[];
}

/** Une nature au tableau de la reprise : `ecart` = total − (tout ce qui est expliqué). */
export interface LigneRepriseGalerie {
  readonly nature: NatureMandat;
  readonly total: number;
  readonly crees: number;
  readonly completes: number;
  readonly ignores: number;
  readonly refuses: number;
  readonly ecartes: number;
  readonly ecart: number;
  readonly aRevoir: number;
}

export type ControleReprise =
  'historique_localisations' | 'echantillon_fiches' | 'images_principales' | 'factures_anterieures';

/**
 * La recette de la reprise vue par la galerie (`console/recette.ts`,
 * `reprise/recette-regie.ts`) : des compteurs et un état, rien de ce que
 * Venio fait pour les obtenir.
 */
export interface RecetteReprise {
  readonly numero: string;
  readonly etat: string;
  readonly natures: readonly LigneRepriseGalerie[];
  readonly aRevoir: number;
  readonly controles: readonly { cle: ControleReprise; coche: boolean; le: string | null }[];
  readonly validation: {
    readonly par: string;
    readonly le: string;
    readonly pv: { readonly disponible: boolean; readonly rangeAuCoffre: boolean };
  } | null;
  readonly signalements: readonly { texte: string; par: string; le: string }[];
  /** La reprise est en recette et la galerie ne l'a pas encore validée. */
  readonly peutValider: boolean;
  readonly basculePrevueLe: string | null;
}

/** `GET …/recette` : `null` quand la galerie n'a aucun dossier de reprise. */
export interface ReponseRecetteReprise {
  readonly recette: RecetteReprise | null;
}

/** `GET …/recette/pv` : une URL signée de courte durée, jamais gardée. */
export interface ProcesVerbalReprise {
  readonly url: string;
  readonly expireLe: string;
  readonly expireDansSecondes: number;
  readonly sha256: string;
}

/** Longueur maximale d'un signalement (API : 2 000 caractères). */
export const SIGNALEMENT_MAX = 2000;

/** Durée maximale d'un accès de support, en heures (engagement 04). */
export const DUREE_SUPPORT_MAX_HEURES = 24;

const base = (galerie: string) => `/g/${encodeURIComponent(galerie)}`;

export const accesVenioApi = {
  mandat: (galerie: string, appel?: AppelApi) =>
    api.get<EtatMandatReprise>(`${base(galerie)}/mandat-reprise`, { galerie, ...appel }),
  accorderMandat: (galerie: string, demandeId: string, appel?: AppelApi) =>
    api.post<MandatReprise>(
      `${base(galerie)}/mandat-reprise/accorder`,
      { demandeId },
      { galerie, ...appel },
    ),
  refuserMandat: (galerie: string, demandeId: string, appel?: AppelApi) =>
    api.post<void>(`${base(galerie)}/mandat-reprise/refuser`, { demandeId }, { galerie, ...appel }),
  revoquerMandat: (galerie: string, appel?: AppelApi) =>
    api.post<void>(`${base(galerie)}/mandat-reprise/revoquer`, {}, { galerie, ...appel }),
  recette: (galerie: string, appel?: AppelApi) =>
    api.get<ReponseRecetteReprise>(`${base(galerie)}/mandat-reprise/recette`, {
      galerie,
      ...appel,
    }),
  validerRecette: (galerie: string, appel?: AppelApi) =>
    api.post<ReponseRecetteReprise>(
      `${base(galerie)}/mandat-reprise/recette/valider`,
      {},
      { galerie, ...appel },
    ),
  signalerRecette: (galerie: string, texte: string, appel?: AppelApi) =>
    api.post<ReponseRecetteReprise>(
      `${base(galerie)}/mandat-reprise/recette/signaler`,
      { texte },
      { galerie, ...appel },
    ),
  procesVerbal: (galerie: string, appel?: AppelApi) =>
    api.get<ProcesVerbalReprise>(`${base(galerie)}/mandat-reprise/recette/pv`, {
      galerie,
      ...appel,
    }),
  support: (galerie: string, appel?: AppelApi) =>
    api.get<EtatSupport>(`${base(galerie)}/support-access`, { galerie, ...appel }),
  accorderSupport: (galerie: string, id: string, dureeHeures: number, appel?: AppelApi) =>
    api.post<{ id: string; actif: true; expireLe: string }>(
      `${base(galerie)}/support-access/${encodeURIComponent(id)}/accorder`,
      { dureeHeures },
      { galerie, ...appel },
    ),
  refuserSupport: (galerie: string, id: string, appel?: AppelApi) =>
    api.post<void>(
      `${base(galerie)}/support-access/${encodeURIComponent(id)}/refuser`,
      {},
      { galerie, ...appel },
    ),
  revoquerSupport: (galerie: string, appel?: AppelApi) =>
    api.delete<void>(`${base(galerie)}/support-access`, { galerie, ...appel }),
};

export function useMandatReprise(galerie: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: cleMetier(galerie, 'acces-venio', 'mandat'),
    queryFn: (contexte) => accesVenioApi.mandat(galerie, appelDeRequete(contexte)),
    enabled: options.enabled ?? true,
  });
}

export function useRecetteReprise(galerie: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: cleMetier(galerie, 'acces-venio', 'recette'),
    queryFn: (contexte) => accesVenioApi.recette(galerie, appelDeRequete(contexte)),
    enabled: options.enabled ?? true,
  });
}

/**
 * N'ouvre rien elle-même : rend l'URL signée du procès-verbal. Une mutation,
 * et non une requête : chaque appel est journalisé, une nouvelle URL à chaque
 * fois est le comportement voulu.
 */
export function useProcesVerbalReprise(galerie: string) {
  return useMutation<ProcesVerbalReprise, Error, void>(
    dansLaPortee({
      mutationFn: (_vide: void, appel) => accesVenioApi.procesVerbal(galerie, appel),
    }),
  );
}

export function useAccesSupport(galerie: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: cleMetier(galerie, 'acces-venio', 'support'),
    queryFn: (contexte) => accesVenioApi.support(galerie, appelDeRequete(contexte)),
    enabled: options.enabled ?? true,
  });
}

/** Les gestes de la page : chacun relit l'état des deux blocs à son issue. */
export function useGestesAccesVenio(galerie: string) {
  const client = useQueryClient();
  const relire = () => client.invalidateQueries({ queryKey: cleMetier(galerie, 'acces-venio') });
  return {
    accorderMandat: useMutation<MandatReprise, Error, string>(
      dansLaPortee({
        mutationFn: (demandeId: string, appel) =>
          accesVenioApi.accorderMandat(galerie, demandeId, appel),
        onSettled: relire,
      }),
    ),
    refuserMandat: useMutation<void, Error, string>(
      dansLaPortee({
        mutationFn: (demandeId: string, appel) =>
          accesVenioApi.refuserMandat(galerie, demandeId, appel),
        onSettled: relire,
      }),
    ),
    revoquerMandat: useMutation<void, Error, void>(
      dansLaPortee({
        mutationFn: (_vide: void, appel) => accesVenioApi.revoquerMandat(galerie, appel),
        onSettled: relire,
      }),
    ),
    validerReprise: useMutation<ReponseRecetteReprise, Error, void>(
      dansLaPortee({
        mutationFn: (_vide: void, appel) => accesVenioApi.validerRecette(galerie, appel),
        onSettled: relire,
      }),
    ),
    signalerReprise: useMutation<ReponseRecetteReprise, Error, string>(
      dansLaPortee({
        mutationFn: (texte: string, appel) => accesVenioApi.signalerRecette(galerie, texte, appel),
        onSettled: relire,
      }),
    ),
    accorderSupport: useMutation<
      { id: string; actif: true; expireLe: string },
      Error,
      { id: string; dureeHeures: number }
    >(
      dansLaPortee({
        mutationFn: ({ id, dureeHeures }: { id: string; dureeHeures: number }, appel) =>
          accesVenioApi.accorderSupport(galerie, id, dureeHeures, appel),
        onSettled: relire,
      }),
    ),
    refuserSupport: useMutation<void, Error, string>(
      dansLaPortee({
        mutationFn: (id: string, appel) => accesVenioApi.refuserSupport(galerie, id, appel),
        onSettled: relire,
      }),
    ),
    revoquerSupport: useMutation<void, Error, void>(
      dansLaPortee({
        mutationFn: (_vide: void, appel) => accesVenioApi.revoquerSupport(galerie, appel),
        onSettled: relire,
      }),
    ),
  };
}
